import { createGlobalState } from '@vueuse/core';
import { ref, nextTick } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { HTTP_INTERNAL_SERVER_ERROR, HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import {
    adminTeamsClient,
    adminUsersClient,
    orgUnitsClient,
    orgUnitUsersClient,
    orgUnitInvitationsClient,
    orgUnitGroupsClient,
} from '~/services';

export const useOrgUnitDetails = createGlobalState(() => {
    const toasted = useToasted();

    const orgUnit = ref({});
    const loading = ref(true);

    const catchRequestError = (error) => {
        console.error(error);
        if (
            !error?.response?.status ||
            error?.response?.status >= HTTP_INTERNAL_SERVER_ERROR ||
            error?.response.status == HTTP_NOT_FOUND
        ) {
            toasted?.global.platform_error();
        } else {
            toasted?.error(error.response?.data?.message || 'Something went wrong.');
        }
    };

    let onMountedOrgUnitCallback = () => {};
    const onMountedOrgUnit = (callback) => {
        onMountedOrgUnitCallback = callback;
    };

    const mount = async (teamId, orgUnitId) => {
        await nextTick();

        team.value = undefined;
        teamOwner.value = undefined;
        invitationsCount.value = undefined;
        usersCount.value = undefined;
        groupsCount.value = undefined;

        if (!teamId || !orgUnitId) {
            orgUnit.value = {};
            loading.value = false;
            onMountedOrgUnitCallback?.();
            return;
        }

        try {
            orgUnit.value.id = orgUnitId;
            orgUnit.value.team_id = teamId;
            loading.value = true;

            const response = await orgUnitsClient.showOrgUnit(teamId, orgUnitId);
            orgUnit.value = response.data;
            loading.value = false;

            if (orgUnit.value.team_id) {
                getTeam().then(() => {
                    if (team.value?.owner_id) {
                        getTeamOwner();
                    }
                });
            }

            onMountedOrgUnitCallback?.();
        } catch (e) {
            catchRequestError(e);
            loading.value = false;
        }
    };

    const unmount = () => {
        orgUnit.value = {};
        team.value = undefined;
        teamOwner.value = undefined;
        invitationsCount.value = undefined;
        usersCount.value = undefined;
        groupsCount.value = undefined;
        loading.value = false;
    };

    const refreshOrgUnit = async (teamId, orgUnitId) => {
        try {
            const response = await orgUnitsClient.showOrgUnit(teamId, orgUnitId);
            orgUnit.value = response.data;
        } catch (e) {
            console.error(e);
        }
    };

    const team = ref(undefined);
    const teamLoading = ref(false);
    const getTeam = async () => {
        if (!orgUnit.value.team_id) {
            return;
        }

        if (team.value || teamLoading.value) {
            return;
        }

        try {
            teamLoading.value = true;
            const response = await adminTeamsClient.getTeam(orgUnit.value.team_id);
            team.value = response.data;
            return response.data;
        } catch (e) {
            catchRequestError(e);
        } finally {
            teamLoading.value = false;
        }
    };

    const teamOwner = ref(undefined);
    const teamOwnerLoading = ref(false);
    const getTeamOwner = async () => {
        if (!team.value?.owner_id) {
            return;
        }

        try {
            teamOwnerLoading.value = true;
            const response = await adminUsersClient.showUser(team.value.owner_id);
            teamOwner.value = response.data;
        } catch (e) {
            catchRequestError(e);
        } finally {
            teamOwnerLoading.value = false;
        }
    };

    const invitationsCount = ref(undefined);
    const invitationsCountLoading = ref(false);
    const getInvitationsCount = async () => {
        try {
            invitationsCountLoading.value = true;
            const response = await orgUnitInvitationsClient.invitationsCount(orgUnit.value.team_id, orgUnit.value.id);
            invitationsCount.value = response.data.count;
        } catch (e) {
            catchRequestError(e);
        } finally {
            invitationsCountLoading.value = false;
        }
    };

    const usersCount = ref(undefined);
    const usersCountLoading = ref(false);
    const getUsersCount = async () => {
        try {
            usersCountLoading.value = true;
            const response = await orgUnitUsersClient.countOrgUnitUsers(orgUnit.value.team_id, orgUnit.value.id);
            usersCount.value = response.data.count;
        } catch (e) {
            catchRequestError(e);
        } finally {
            usersCountLoading.value = false;
        }
    };

    const groupsCount = ref(undefined);
    const groupsCountLoading = ref(false);
    const getGroupsCount = async () => {
        try {
            groupsCountLoading.value = true;
            const response = await orgUnitGroupsClient.countOrgUnitGroups(orgUnit.value.team_id, orgUnit.value.id);
            groupsCount.value = response.data.count;
        } catch (e) {
            catchRequestError(e);
        } finally {
            groupsCountLoading.value = false;
        }
    };

    return {
        loading,
        orgUnit,
        mount,
        unmount,
        refreshOrgUnit,
        onMountedOrgUnit,

        team,
        teamLoading,
        getTeam,

        teamOwner,
        teamOwnerLoading,
        getTeamOwner,

        invitationsCount,
        invitationsCountLoading,
        getInvitationsCount,

        usersCount,
        usersCountLoading,
        getUsersCount,

        groupsCount,
        groupsCountLoading,
        getGroupsCount,
    };
});
